/**
 * @fileoverview Invitation verification component
 *
 * This file implements the invitation verification process that occurs when
 * a user clicks on an invitation link. It verifies the token from the URL,
 * determines the user type, and redirects to the appropriate page based on
 * whether the user already has an Okta account.
 *
 * Key features:
 * - Token validation from URL
 * - User type determination (Employee or Candidate)
 * - Conditional redirection based on account existence
 * - Loading state management during verification
 * - Error handling for invalid or expired invitations
 */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Api from "../Api";
import Loader from "../commons/Loader";
import { getOnboardingImg, getHeaderLogo, getToken } from "../commons/commonFunctions";
import Header from '../commons/Header';
import Footer from '../commons/Footer';
import { globalContext } from "../store";

/**
 * Invitation verification component
 *
 * This component serves as a gateway that verifies invitation tokens and directs users
 * to the appropriate next step in the onboarding process. While verification is in progress,
 * it displays a loading indicator. If verification fails, it shows an unauthorized access message.
 *
 * @returns {JSX.Element} Loading indicator during verification or error message if verification fails
 */
const InviteVerify = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<any>(false);
  const [, setErrorMsg] = useState<any>(false);
  const { globalState } = useContext(globalContext);
  const title = globalState?.clientData?.name;

  /**
   * Determine the appropriate subdomain for API requests
   * Uses a test domain for local development, otherwise uses the current host
   */
  let subDomain: any =
    window.location.host === "localhost:3000"
      ? "validation.test-leaders.heidricknavigator.com"
      : window.location.host;
  /**
   * Extract verification token from URL using utility function
   */
  const token = getToken(window.location.href);
  const currentDate = new Date();
  const payload = {
    token: token,
    subDomain: subDomain,
    expiryDate: `${String(currentDate.getDate()).padStart(2, "0")}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${currentDate.getFullYear()}`,
  };

  /**
   * Effect that runs on component mount to verify the invitation token
   * 
   * This effect:
   * 1. Calls the invitation verification API with the token
   * 2. Processes the response to determine user type and account status
   * 3. Redirects to the appropriate page based on user type and account existence
   * 4. Sets error state if verification fails
   */
  useEffect(() => {
    Api.inviteVerifyNew(payload).then((response: any) => {
      if (response?.success) {
        // Extract relevant data from the response
        const data = response?.response?.data?.data;
        const token = data?.data?.Token;
        const oktaUserId = data?.data?.okta_user_id;
        const route = `/set-password?token=${token}`;
        navigate(route, {
          state: { userDetails: { oktaUserId: oktaUserId } },
        });
        setError(false);
      } else {
        if (response?.data?.detail === "Token is not active") {
          window.location.href = '/';
        }
        // Set error state if verification fails
        setError(true);
        setErrorMsg(response.data.detail || "Invitation verification failed.");
      }
    });
  }, []);
  return (
    <>
      {!error ? (
        <Loader />
      ) : (
        <div className='mainWrapperOnboarding' style={{ backgroundImage: `url(${getOnboardingImg(globalState)})`, }}>
          <div className='middleContentWrapper'>
            {/* Header with client logo */}
            <Header title={title} headerLogo={getHeaderLogo(globalState)} />
            <hr className='headerSeparator' />

            {/* Welcome content section */}
            <div className='contentWrapper' style={{ alignItems: 'unset', padding:'32px 40px'}}>
              <h2 style={{ marginBottom:'24px' }}>Link Expired - Check your email</h2>
              <div className="welcomeContent" style={{ color: '#334255' }}>This link has expired. <br/> For security, activation links expire after 7 days. A new invitation has been sent to your email automatically. If it doesn't arrive, check your spam or junk folder. For more help, contact your IT administrator or <a href="mailto:support@heidrick.com">support@heidrick.com</a></div>
            </div>

            {/* Page footer */}
            <Footer />
          </div>
        </div>
      )}
    </>
  );
};

export default InviteVerify;
